import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { formatLocation } from "../lib/cities";
import { PROVINCE_NAME } from "../lib/constants";
import { ListingCard } from "./ListingCard";
import { ArrowRight, Map as MapIcon, Siren } from "lucide-react";

export const NEAR_CITY_EVENT = "cp-near-city";
export const NEAR_RADIUS_EVENT = "cp-near-radius";

export function LostNearYou({ onItems }) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const pickKey = `cp_near_city_${user?.user_id || "anon"}`;
  const [picked, setPicked] = useState(null);
  const [data, setData] = useState(null);
  const radKey = `cp_near_radius_${user?.user_id || "anon"}`;
  const [radius, setRadius] = useState(null);

  useEffect(() => {
    if (user === null) return;
    setPicked(localStorage.getItem(pickKey) || "");
    setRadius(localStorage.getItem(radKey) || "province");
    const onPick = (e) => setPicked(e.detail || "");
    const onRad = (e) => setRadius(e.detail || "province");
    window.addEventListener(NEAR_CITY_EVENT, onPick);
    window.addEventListener(NEAR_RADIUS_EVENT, onRad);
    return () => { window.removeEventListener(NEAR_CITY_EVENT, onPick); window.removeEventListener(NEAR_RADIUS_EVENT, onRad); };
  }, [pickKey, radKey, user]);

  useEffect(() => {
    if (picked === null || radius === null) return;
    const qs = `?radius=${radius}${picked ? `&city=${encodeURIComponent(picked)}` : ""}`;
    api.get(`/listings/nearby-lost${qs}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [picked, radius]);
  useEffect(() => { onItems?.(data?.origin ? data.items.map((l) => l.id) : []); }, [data, onItems]);

  if (!data?.origin || !data.items.length) return null;
  const label = formatLocation(data.origin.city, data.origin.province);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16" data-testid="lost-near-you">
      <div className="rounded-3xl border border-red-100 bg-gradient-to-br from-red-50 via-white to-orange-50 p-5 sm:p-7">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#12234A] flex items-center gap-2">
              <span className="h-9 w-9 rounded-full bg-red-600 text-white flex items-center justify-center animate-pulse"><Siren size={18} /></span>
              {t("lny_title")}
            </h2>
            <p className="text-slate-600 mt-1 text-sm" data-testid="lost-near-you-origin">
              {data.scope === "province" ? t("lny_in_prov").replace("{prov}", PROVINCE_NAME[data.origin.province] || data.origin.province) : t("lny_sub").replace("{km}", data.radius_km)} <b className="text-[#12234A]">{label}</b>
            </p>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/lost-found/map" data-testid="lost-near-you-map" className="text-sm font-semibold text-slate-600 hover:text-[#12234A] flex items-center gap-1">
              <MapIcon size={16} /> {t("lny_map")}
            </Link>
            <Link to={data.scope === "province" ? `/browse?category=lost-found&lf=lost&province=${data.origin.province}` : `/browse?category=lost-found&lf=lost&near=${encodeURIComponent(label)}&radius=${Math.min(100, data.radius_km)}`} data-testid="lost-near-you-see-all"
              className="text-red-600 font-semibold flex items-center gap-1 hover:gap-2 transition-all">
              {t("lny_see_all")} <ArrowRight size={18} />
            </Link>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.items.map((l) => <ListingCard key={l.id} listing={l} />)}
        </div>
      </div>
    </section>
  );
}
